<template>
  <div v-if="obfuscatedToken">
    <div class="font-bold"> SCIM Enforcement </div>
    <div class="text-neutral-text">
      Control whether user management is handled exclusively by your Identity
      Provider.
    </div>
    <BittsRadioGroupCards
      :initial-value="scimEnforcement"
      :options="SCIM_ENFORCEMENT_OPTIONS"
      :disabled="scimStatus === 'disabled'"
      data-testid="require-scim-group"
      class="mb-16 mt-16"
      @change="scimEnforcement = !scimEnforcement"
    />
  </div>
  <SCIMConfigModal
    v-if="showScimConfigModal"
    :scim-base-url="scimBaseUrl"
    :scim-token="scimToken"
    @closed="showScimConfigModal = false"
  />
</template>

<script setup lang="ts">
import { BittsRadioGroupCards } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import { SCIM_ENFORCEMENT_OPTIONS } from '@/constants/me';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { SsoConfigSetup } from '@/types/sso_config';

import SCIMConfigModal from './SCIMConfigModal.vue';

const props = defineProps<{
  ssoConfigData: SsoConfigSetup | null;
  ssoEnabled: boolean;
}>();

const emit = defineEmits<{
  refreshConfig: [];
}>();

const flashesStore = useFlashesStore();

const scimBaseUrl = ref<string | null>(null);
const scimToken = ref<string | null>(null);
const obfuscatedToken = ref<string | null>(null);
const scimEnforcement = ref(false);
const showScimConfigModal = ref(false);
const scimTokenCreatedAt = ref<DateTime | null>(null);

const scimStatus = computed(() => {
  // If SSO is disabled, SCIM is always disabled
  if (!props.ssoEnabled) {
    return 'disabled';
  }
  const provisioningStatus = props.ssoConfigData?.scim_provisioning;
  if (provisioningStatus) {
    return provisioningStatus;
  }
  return 'disabled';
});

const tokenGeneratedTime = computed(() => {
  if (!scimTokenCreatedAt.value) return null;
  return scimTokenCreatedAt.value.toRelative();
});

type SsoFormValues = {
  signOnUrl?: string;
  certificate?: string;
  samlMetadataUrl?: string;
  samlIdpIssuer?: string;
  oidcIssuer?: string;
  pickedSSOCoreRole?: string | null;
  pickedSSOSalesRole?: string | null;
};

async function handleConfigureScim(currentFormData?: SsoFormValues) {
  // Use current form data if provided, otherwise fall back to props
  const loginUrl = currentFormData?.signOnUrl ?? props.ssoConfigData?.login_url;
  const certificateValue =
    currentFormData?.certificate ?? props.ssoConfigData?.certificate;
  const metadataUrl =
    currentFormData?.samlMetadataUrl ?? props.ssoConfigData?.saml_metadata_url;
  const samlIdpIssuer =
    currentFormData?.samlIdpIssuer ?? props.ssoConfigData?.saml_idp_issuer;
  const oidcIssuer =
    currentFormData?.oidcIssuer ?? props.ssoConfigData?.oidc_issuer;
  const defaultRole =
    currentFormData?.pickedSSOCoreRole ?? props.ssoConfigData?.default_role;
  const defaultSalesRole =
    currentFormData?.pickedSSOSalesRole ??
    props.ssoConfigData?.default_sales_edge_role;

  if (!metadataUrl && (!loginUrl || !certificateValue)) return;

  const body = {
    ...(metadataUrl
      ? { saml_metadata_url: metadataUrl }
      : {
          login_url: loginUrl,
          certificate: certificateValue,
          saml_idp_issuer: samlIdpIssuer || null,
          oidc_issuer: oidcIssuer || null,
        }),
    default_core_role_id: defaultRole ?? null,
    default_sales_edge_role: defaultSalesRole ?? null,
    scim_provisioning: 'enabled' as const,
  };

  const { data, error } = await crossbeamApi.PUT('/v0.2/sso/config', {
    body,
  });

  if (error) {
    captureException(error);
    flashesStore.addErrorFlash({
      message: 'Failed to configure SCIM',
    });
    return;
  }

  if (data) {
    scimBaseUrl.value = data.scim_url;
  }

  const { data: tokenData, error: tokenError } = await crossbeamApi.POST(
    '/v0.1/sso/scim-token',
  );

  if (tokenError) {
    captureException(tokenError);
    flashesStore.addErrorFlash({
      message: 'Failed to generate SCIM token',
    });
    return;
  }

  if (tokenData) {
    scimToken.value = tokenData.token;
  }

  emit('refreshConfig');
}

async function handleOpenScimConfigModal(currentFormData?: SsoFormValues) {
  await handleConfigureScim(currentFormData);
  if (scimToken.value) {
    showScimConfigModal.value = true;
  }
}

async function handleRevokeToken() {
  const { error } = await crossbeamApi.DELETE('/v0.1/sso/scim-token');

  if (error) {
    captureException(error);
    flashesStore.addErrorFlash({
      message: 'Failed to revoke SCIM token',
    });
    return;
  }
  obfuscatedToken.value = null;
  scimToken.value = null;
  emit('refreshConfig');
}

// Initialize token-related data from props
watch(
  () => props.ssoConfigData,
  (configData) => {
    if (configData) {
      obfuscatedToken.value = configData.obfuscated_token ?? null;
      if (configData.scim_token_created_at) {
        const dateTime = DateTime.fromISO(configData.scim_token_created_at);
        if (dateTime.isValid) {
          scimTokenCreatedAt.value = dateTime;
        }
      }
    }
  },
  { immediate: true },
);

// Update scimEnforcement only when scim_provisioning changes in props
watch(
  () => props.ssoConfigData?.scim_provisioning,
  (provisioningStatus) => {
    // Only update when we have an explicit provisioning status (not undefined/null)
    if (provisioningStatus) {
      scimEnforcement.value = provisioningStatus === 'enforced';
    }
  },
  { immediate: true },
);

// Expose methods and state for parent component
defineExpose({
  handleOpenScimConfigModal,
  scimEnforcement,
  obfuscatedToken,
  tokenGeneratedTime,
  handleRevokeToken,
});
</script>
